import { JSXElementConstructor, ReactElement, useState } from 'react';
import { AutocompleteRenderInputParams, TextField } from '@hopskipdrive/react-components';
import { PopperProps } from '@mui/material/Popper';

import { useGetSubClassifications } from '../../hooks/useIncidentApi';
import { IncidentSubclassification } from '../../types/incidents';

import {
  IncidentItem,
  StyledAutocomplete,
  StyledIncidentSubClassification,
} from './IncidentSubClassificationDropdown.styles';

export interface IncidentSubClassificationDropdownProps {
  value: IncidentSubclassification | null;
  onChange: (value: IncidentSubclassification | null) => void;
  classificationId?: number;
  requiresSubclassification?: boolean;
  subclassificationsCount?: number;
  open?: boolean;
  error?: boolean;
  helperText?: string;
  sortField?: string;
  popper?: JSXElementConstructor<PopperProps>;
}

const IncidentSubClassificationDropdown = ({
  value,
  classificationId,
  requiresSubclassification,
  subclassificationsCount,
  onChange,
  open,
  error,
  helperText,
  sortField,
  popper,
}: IncidentSubClassificationDropdownProps): ReactElement => {
  const { isFetching, data: response } = useGetSubClassifications({
    sortDir: 'asc',
    sortField: sortField || 'name',
  });

  // Sublassifications are filtered based on the selected classification
  const filteredSubClassifcations = (value) => {
    if (!value) {
      return null;
    }

    if (!classificationId) {
      return value;
    }

    return value.filter((item) => item.classificationId === classificationId);
  };

  const [inputValue, setInputValue] = useState<string>('');

  const filterOptionsOnSearch = (options) => {
    if (!value && inputValue) {
      const lowercasedSearchString = inputValue?.toLowerCase();

      let stringMatch = false;

      options = options.filter((option) => {
        stringMatch = option.name.toLowerCase().includes(lowercasedSearchString);

        return stringMatch;
      });
    }

    return options;
  };
  const getHelperText = () => {
    if (subclassificationsCount === 0) {
      return 'This Classification does not have any Subclassifications';
    } else if (requiresSubclassification) {
      return 'A Subclassification is required for this Classification';
    }

    return helperText;
  };

  return (
    <StyledAutocomplete
      options={filteredSubClassifcations(response?.data) || []}
      open={open}
      isLoading={isFetching}
      disabled={subclassificationsCount === 0 && !requiresSubclassification}
      filterOptions={filterOptionsOnSearch}
      inputValue={inputValue}
      onInputChange={(_, input) => !value && setInputValue(input)}
      getOptionLabel={(_) => ''}
      value={value}
      PopperComponent={popper}
      onChange={(_, selected) => {
        onChange(selected || null);
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          variant="outlined"
          error={error}
          helperText={getHelperText()}
          placeholder={value ? '' : 'Select Subclassification'}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && value) {
              onChange(null);
            }
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {params.InputProps.startAdornment}
                {value ? (
                  <StyledIncidentSubClassification
                    word-wrap="break-word"
                    overflow-wrap="break-word"
                    className={value.name}
                  >
                    {value.name}
                  </StyledIncidentSubClassification>
                ) : null}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <IncidentItem
            {...props}
            key={option.name}
            className={value?.name === option.name ? 'selected classification' : 'classification'}
          >
            <StyledIncidentSubClassification>{option.name}</StyledIncidentSubClassification>
          </IncidentItem>
        );
      }}
    />
  );
};

export default IncidentSubClassificationDropdown;
